import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Card,
  Flex,
  Input,
  List,
  Select,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import {
  activateVersion,
  getPrompt,
  getPromptVersion,
  savePrompt,
} from '@/api/ai-chat/prompt'
import { getErrorMessage } from '@/utils/getErrorMessage'
import type { PromptVersion } from '@/api/ai-chat/types'
import { diffLines, fmtTime } from './utils'
import { DiffModal } from './DiffModal'
import type { DiffLine } from './utils'

const { Title, Text } = Typography

const TYPE_OPTIONS = [
  { value: 'web', label: 'Web' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
]

const TYPE_FILTERS = ['all', 'web', 'sms', 'email'] as const

export const PromptEditorPage: React.FC = () => {
  const [versions, setVersions] = useState<PromptVersion[]>([])
  const [promptType, setPromptType] = useState('web')
  const [promptText, setPromptText] = useState('')
  const [promptName, setPromptName] = useState('')
  const [notes, setNotes] = useState('')
  const [versionTypeFilter, setVersionTypeFilter] = useState<string>('all')
  const [versionSearch, setVersionSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [diffOpen, setDiffOpen] = useState(false)
  const [diffTitle, setDiffTitle] = useState('')
  const [diffLines_, setDiffLines] = useState<DiffLine[]>([])

  const load = useCallback(async (type: string) => {
    setLoading(true)
    try {
      const data = await getPrompt(type)
      if (data.active) setPromptText(data.active.system_prompt)
      else setPromptText('')
      setVersions(data.versions)
      setPromptName('')
      setNotes('')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load('web')
  }, [load])

  const nextVersionNum = useMemo(() => {
    const name = promptName.trim()
    if (!name) return null
    const max = versions
      .filter((v) => v.name === name)
      .reduce((m, v) => Math.max(m, v.version_num ?? 0), 0)
    return max + 1
  }, [promptName, versions])

  const filteredVersions = useMemo(() => {
    const kw = versionSearch.trim().toLowerCase()
    return versions
      .filter(
        (v) =>
          versionTypeFilter === 'all' ||
          (v.type || 'web') === versionTypeFilter,
      )
      .filter(
        (v) =>
          !kw ||
          (v.name || '').toLowerCase().includes(kw) ||
          (v.version || '').toLowerCase().includes(kw) ||
          (v.notes || '').toLowerCase().includes(kw),
      )
  }, [versions, versionTypeFilter, versionSearch])

  const handleTypeChange = (type: string) => {
    setPromptType(type)
    load(type)
  }

  const handleLoadVersion = async (id: number) => {
    const full = await getPromptVersion(id)
    if (full?.system_prompt) {
      setPromptText(full.system_prompt)
      if (full.type) setPromptType(full.type)
      setPromptName(full.name || '')
      setNotes('')
    }
  }

  const handleSave = async () => {
    const name = promptName.trim()
    if (!name) {
      message.warning('Enter a name first')
      return
    }
    if (!promptText.trim()) {
      message.warning('Prompt cannot be empty')
      return
    }
    setSaving(true)
    try {
      const saved = await savePrompt({
        name,
        notes: notes.trim(),
        system_prompt: promptText.trim(),
        type: promptType,
      })
      const label = saved.name
        ? `${saved.name} v${saved.version_num ?? ''}`
        : saved.version
      message.success('Saved ' + label)
      load(promptType)
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to save prompt'))
    } finally {
      setSaving(false)
    }
  }

  const handleActivate = async (v: PromptVersion) => {
    let msg: string
    if (v.name && v.version_num) {
      const currentDefault = versions.find(
        (x) =>
          x.name === v.name &&
          (x.type || 'web') === (v.type || 'web') &&
          x.is_active,
      )
      const title = `Make version ${v.version_num} of ${v.name} the default?`
      const body = currentDefault
        ? `This will replace the current default for ${v.name}, currently set to version ${currentDefault.version_num}.`
        : `This will set the default for ${v.name}.`
      msg = `${title}\n\n${body}`
    } else {
      msg = `Make "${v.version}" the default?\n\nThis will replace the current default for its type.`
    }
    if (!window.confirm(msg)) return
    try {
      await activateVersion(v.id)
      const label = v.name ? `${v.name} v${v.version_num}` : v.version
      message.success('Set as default: ' + label)
      load(promptType)
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to set as default'))
    }
  }

  const handleTest = (v: PromptVersion) => {
    const agentBase = import.meta.env.VITE_AI_AGENT_API_URL || ''
    const chatBase =
      v.type === 'sms'
        ? `${agentBase}/chat/victoria/sms/`
        : `${agentBase}/chat/victoria/`
    let url: string
    if (v.name && v.version_num) {
      const qs = new URLSearchParams({ n: v.name, v: String(v.version_num) })
      if (v.type && v.type !== 'web') qs.set('t', v.type)
      url = `${chatBase}?${qs}`
    } else {
      url = `${chatBase}?prompt_version=${encodeURIComponent(v.version)}`
    }
    window.open(url, '_blank')
  }

  const handleShowDiff = async (selectedId: number) => {
    const active = versions.find((v) => v.is_active)
    if (!active) {
      message.warning('No active version found')
      return
    }

    let oldId: number, newId: number, oldLabel: string, newLabel: string
    if (selectedId === active.id) {
      const idx = versions.findIndex((v) => v.id === selectedId)
      const prev = versions[idx + 1]
      if (!prev) {
        message.warning('No previous version to compare against')
        return
      }
      oldId = prev.id
      oldLabel = prev.version
      newId = active.id
      newLabel = active.version + ' (active)'
    } else {
      const sel = versions.find((v) => v.id === selectedId)
      oldId = selectedId
      oldLabel = sel?.version ?? String(selectedId)
      newId = active.id
      newLabel = active.version + ' (active)'
    }

    setDiffTitle('Loading…')
    setDiffLines([])
    setDiffOpen(true)

    const [oldFull, newFull] = await Promise.all([
      getPromptVersion(oldId),
      getPromptVersion(newId),
    ])
    setDiffTitle(`${oldLabel}  →  ${newLabel}`)
    setDiffLines(diffLines(oldFull.system_prompt, newFull.system_prompt))
  }

  return (
    <Spin spinning={loading}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Prompt Editor
        </Title>
        <Button icon={<ReloadOutlined />} onClick={() => load(promptType)}>
          Refresh
        </Button>
      </Flex>

      <Card
        title="Active Prompt — Save as New Version"
        style={{ marginBottom: 16 }}
      >
        <Flex gap={8} style={{ marginBottom: 12 }}>
          <Select
            value={promptType}
            onChange={handleTypeChange}
            options={TYPE_OPTIONS}
            style={{ width: 100 }}
          />
          <Input
            value={promptName}
            onChange={(e) => setPromptName(e.target.value.replace(/\s+/g, '-'))}
            placeholder="Name (e.g. v.spin)"
            style={{ maxWidth: 200 }}
          />
          <Input
            value={nextVersionNum != null ? String(nextVersionNum) : '—'}
            readOnly
            tabIndex={-1}
            style={{ maxWidth: 72, color: '#6b7280', cursor: 'default' }}
          />
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)"
            style={{ flex: 1 }}
          />
        </Flex>

        <Input.TextArea
          value={promptText}
          onChange={(e) => setPromptText(e.target.value)}
          placeholder="Loading…"
          rows={18}
          style={{
            fontFamily: '"SF Mono", "Fira Code", monospace',
            fontSize: 12.5,
            lineHeight: 1.6,
          }}
        />

        <Flex align="center" gap={8} style={{ marginTop: 12 }}>
          <Button type="primary" loading={saving} onClick={handleSave}>
            Save Version
          </Button>
          <Button onClick={() => load(promptType)}>Reset</Button>
        </Flex>
      </Card>

      <Card title="Version History">
        <Flex gap={8} align="center" style={{ marginBottom: 12 }}>
          {TYPE_FILTERS.map((f) => (
            <Button
              key={f}
              size="small"
              type={versionTypeFilter === f ? 'primary' : 'default'}
              onClick={() => setVersionTypeFilter(f)}
              style={{ borderRadius: 99 }}
            >
              {f === 'all' ? 'All' : f.toUpperCase()}
            </Button>
          ))}
          <Input
            value={versionSearch}
            onChange={(e) => setVersionSearch(e.target.value)}
            placeholder="Filter versions…"
            allowClear
            style={{ flex: 1 }}
            size="small"
          />
        </Flex>

        <List
          dataSource={filteredVersions}
          rowKey="id"
          size="small"
          locale={{ emptyText: 'No versions for this type.' }}
          renderItem={(v) => {
            const label = v.name ? (
              <Flex align="center" gap={6}>
                <Text strong style={{ fontSize: 13 }}>
                  {v.name}
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {v.version_num}
                </Text>
              </Flex>
            ) : (
              <Text strong style={{ fontSize: 13 }}>
                {v.version}
              </Text>
            )

            return (
              <List.Item
                style={{
                  cursor: 'pointer',
                  border: v.is_active
                    ? '1px solid #2d9e5f'
                    : '1px solid #e2e5ea',
                  borderRadius: 6,
                  marginBottom: 8,
                  padding: '8px 12px',
                  background: v.is_active ? '#f0fdf4' : undefined,
                }}
                onClick={() => handleLoadVersion(v.id)}
                actions={[
                  <Button
                    key="diff"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleShowDiff(v.id)
                    }}
                  >
                    Diff
                  </Button>,
                  <Button
                    key="test"
                    size="small"
                    style={{ color: '#6b21a8', borderColor: '#e9d5ff' }}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleTest(v)
                    }}
                  >
                    Test
                  </Button>,
                  ...(!v.is_active
                    ? [
                        <Button
                          key="activate"
                          size="small"
                          style={{ color: '#166534', borderColor: '#bbf7d0' }}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleActivate(v)
                          }}
                        >
                          Make Default
                        </Button>,
                      ]
                    : []),
                ]}
              >
                <Flex align="center" gap={8} style={{ flex: 1, minWidth: 0 }}>
                  {label}
                  <Tag
                    color={
                      v.type === 'sms'
                        ? 'purple'
                        : v.type === 'email'
                          ? 'orange'
                          : 'blue'
                    }
                    style={{ fontSize: 10 }}
                  >
                    {(v.type || 'web').toUpperCase()}
                  </Tag>
                  {v.is_active && (
                    <Tag color="success" style={{ fontSize: 10 }}>
                      DEFAULT
                    </Tag>
                  )}
                  <Text
                    type="secondary"
                    style={{
                      flex: 1,
                      fontSize: 12,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {v.notes ?? ''}
                  </Text>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, flexShrink: 0 }}
                  >
                    {fmtTime(v.created_at)}
                  </Text>
                </Flex>
              </List.Item>
            )
          }}
        />
      </Card>

      <DiffModal
        open={diffOpen}
        title={diffTitle}
        lines={diffLines_}
        onClose={() => setDiffOpen(false)}
      />
    </Spin>
  )
}
